import React, { useEffect, useState, useRef } from 'react';
import { Button, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { getAllOrdersOfAdmin } from '../redux/actions/order';
import { Link } from 'react-router-dom';
import AdminHeader from '../components/Layout/AdminHeader';
import AdminSideBar from '../components/Admin/Layout/AdminSideBar';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { server } from '../server';
import axios from 'axios';

const AdminDashboardOrders = () => {
  const dispatch = useDispatch();
  const { adminOrders, adminOrderLoading } = useSelector((state) => state.order);
  const [rows, setRows] = useState([]);
  const [orderUpdatesMap, setOrderUpdatesMap] = useState({});
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const limit = 500;
  const cachedOrders = useRef([]);

  // Filter and Sort persistence
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    // Load saved filters only if navigating back (not on fresh page load)
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedFilter = sessionStorage.getItem('adminDashboardOrdersFilterModel');
      const savedSort = sessionStorage.getItem('adminDashboardOrdersSortModel');
      
      if (savedFilter) {
        setFilterModel(JSON.parse(savedFilter));
      }
      if (savedSort) {
        setSortModel(JSON.parse(savedSort));
      }
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem('adminDashboardOrdersFilterModel');
      sessionStorage.removeItem('adminDashboardOrdersSortModel');
    }
  }, []);

  useEffect(() => {
    if (cachedOrders.current.length === 0) {
      dispatch(getAllOrdersOfAdmin(limit));
    }
  }, [dispatch]);

  useEffect(() => {
    if (adminOrders.length > 0) {
      cachedOrders.current = adminOrders;
      setRows(
        adminOrders.map((order) => ({
          id: order._id,
          sellerName: order.cart[0]?.shopId?.name || 'N/A',
          userName: order.user?.name || 'N/A',
          sellerPhone: order.cart[0]?.shopId?.phoneNumber || 'N/A',
          itemsQty: order.cart.reduce((acc, item) => acc + item.qty, 0),
          total: `${order.shopTotalOrderPriceWithCommission.toFixed(2)} Rs`,
          status: order.status,
          createdAt: order.createdAt.slice(0, 10),
          notes: order.notes || '',
          customerTrackingReference: order.customerTrackingReference || '', 
        }))
      );
    }
  }, [adminOrders]);

  const loadOlderOrders = async () => {
    setLoading(true);
    setOffset(offset + limit);
    await dispatch(
      getAllOrdersOfAdmin(limit, cachedOrders.current[cachedOrders.current.length - 1]?.createdAt)
    );
    setLoading(false);
  };

  const loadNewerOrders = async () => {
    setLoading(true);
    setOffset(0);
    await dispatch(getAllOrdersOfAdmin(limit, null, true));
    setLoading(false);
  };

  const handleNoteChange = (id, updates) => {
    setOrderUpdatesMap(prev => ({
      ...prev,
      [id]: {
        ...prev[id],  // Preserve existing values
        ...updates     // Merge new updates
      }
    }));
  };
  
  
  const handleNotesUpdate = async (id) => {
    const updates = orderUpdatesMap[id];
    if (!updates) return;
  
    try {
      await axios.put(
        `${server}/order/admin/update-order-tracking/${id}`,
        { 
          notes: updates.notes,
          customerTrackingReference: updates.customerTrackingReference
        },
        { withCredentials: true }
      );
  
      // Force reload all orders without date filtering
      dispatch(getAllOrdersOfAdmin(limit, null, true)); // Third param 'true' resets the cache
  
      // Clear local state
      setOrderUpdatesMap(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    } catch (error) {
      console.error('Failed to update order details', error);
    }
  };
  
  const columns = [
    { field: 'id', headerName: 'ID', width: 120 },
    { field: 'sellerName', headerName: 'Seller Name', width: 120 },
    {field: 'userName', headerName: 'Customer Name', width: 120 },
    { field: 'sellerPhone', headerName: 'Phone Number', width: 100 },
    { field: 'total', headerName: 'Total', width: 160 },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => {
        let bgColor = '';
        const statusVal = String(params.value).toLowerCase();
        if (statusVal === 'delivered') {
          bgColor = '#d4edda'; // light green
        } else if (statusVal === 'processing') {
          bgColor = '#ffffff'; // white
        } else if (statusVal.includes('packed') || statusVal.includes('ready for dispatch')) {
          bgColor = '#fff3cd'; // light yellow
        } else {
          bgColor = '#ffffff';
        }
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: bgColor,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '0.5rem'
            }}
          >
            {params.value}
          </div>
        );
      },
    },
    { field: 'createdAt', headerName: 'Created At', width: 100 },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      renderCell: (params) => (
        <TextField
          multiline
          fullWidth
          value={orderUpdatesMap[params.id]?.notes ?? params.row.notes}
          onChange={(e) => handleNoteChange(params.id, { 
            notes: e.target.value 
          })}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.stopPropagation(); // Prevent grid navigation
            }
          }}
          variant="outlined"
          size="small"
          style={{ fontSize: '0.8rem' }}
        />
      ),
    },
    {
      field: 'saveNotes',
      headerName: 'Save Notes',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleNotesUpdate(params.id)}
          variant="contained"
          color="primary"
          size="small"
          style={{ fontSize: '0.7rem' }}
        >
          Save
        </Button>
      ),
    },
    {
      field: 'view',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Link to={`/admin/order/${params.row.id}`}>
          <Button>
            <AiOutlineArrowRight size={20} />
          </Button>
        </Link>
      ),
    },
    {
      field: 'customerTrackingReference',
      headerName: 'Tracking Reference',
      width: 200,
      renderCell: (params) => (
        <TextField
          multiline
          fullWidth
          value={orderUpdatesMap[params.id]?.customerTrackingReference ?? params.row.customerTrackingReference}
          onChange={(e) => handleNoteChange(params.id, { 
            customerTrackingReference: e.target.value 
          })}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.stopPropagation(); // Prevent grid navigation
            }
          }}
          variant="outlined"
          size="small"
          style={{ fontSize: '0.8rem' }}
        />
      ),
    },
  ];

  return (
    <div className="dashboard-page-wrapper">
      <AdminHeader />
      <div className="dashboard-flex-wrapper">
        <div className="w-[80px] 800px:w-[330px] dashboard-sidebar">
          <AdminSideBar active={2} />
        </div>
        <div className="dashboard-main-content flex-1">
          <div className="w-full">
            <div className="flex justify-between mb-4 flex-wrap gap-2">
              <Button onClick={loadNewerOrders} variant="contained" size="small">
                View New Orders
              </Button>
              <Button onClick={loadOlderOrders} variant="contained" size="small">
                Show Older Orders
              </Button>
            </div>
            {adminOrderLoading || loading ? (
              <p>Loading orders...</p>
            ) : (
              <div className="datagrid-scroll-wrapper">
                <div className="table-inner-container" style={{ minWidth: "1300px" }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={20}
                    disableSelectionOnClick
                    autoHeight
                    filterModel={filterModel}
                    onFilterModelChange={(newFilterModel) => {
                      setFilterModel(newFilterModel);
                      sessionStorage.setItem('adminDashboardOrdersFilterModel', JSON.stringify(newFilterModel));
                    }}
                    sortModel={sortModel}
                    onSortModelChange={(newSortModel) => {
                      setSortModel(newSortModel);
                      sessionStorage.setItem('adminDashboardOrdersSortModel', JSON.stringify(newSortModel));
                    }}
                    sx={{
                      fontSize: "0.8rem",
                      border: "none",
                      "& .MuiDataGrid-cell": { fontSize: "0.8rem", padding: "0.5rem" },
                      "& .MuiDataGrid-columnHeader": {
                        fontSize: "0.8rem",
                        padding: "0.5rem",
                      },
                      "& .MuiDataGrid-virtualScroller": {
                        overflow: "visible !important",
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardOrders;
